import Phaser from 'phaser';
import type { EnemyToken } from '../entities/enemy';
import type { PartnerType } from './shopCatalog';

export type PartnerShape = Phaser.GameObjects.Arc | Phaser.GameObjects.Rectangle | Phaser.GameObjects.Triangle;

export type PartnerRuntime = {
  type: PartnerType;
  level: PartnerLevel;
  graphic: PartnerShape;
  nextActionAt: number;
  busyUntil: number;
  turretX?: number;
  turretY?: number;
  lastEnemyInRangeAt?: number;
  actionCounter?: number;
  buffUntil?: number;
  buffDamageBonus?: number;
};

export type PartnerLevel = 1 | 2 | 3 | 4 | 5;

export type PartnerSlotSnapshot = {
  type: PartnerType;
  level: PartnerLevel;
};

export function assertPartnerLevel(level: number): PartnerLevel {
  if (!Number.isInteger(level) || level < 1 || level > 5) {
    throw new Error(`Invalid partner level: ${level}`);
  }
  return level as PartnerLevel;
}

export type ProjectileKind = 'bullet' | 'homing';

export type ProjectileData = {
  kind: ProjectileKind;
  damage: number;
  speed: number;
  expiresAt: number;
  target?: EnemyToken;
  sourceType: PartnerType;
  pierceRemaining: number;
  splitOnHit?: boolean;
  explosionRadius?: number;
  explosionDamage?: number;
};

export type PartnerSpec = {
  type: PartnerType;
  color: number;
  shape: 'circle' | 'square' | 'triangle' | 'diamond';
  radius: number;
  offset: Phaser.Math.Vector2;
};

export const PARTNER_SPECS: Record<PartnerType, PartnerSpec> = {
  gunner: {
    type: 'gunner',
    color: 0x4d8dff,
    shape: 'circle',
    radius: 7,
    offset: new Phaser.Math.Vector2(-30, 28),
  },
  sword: {
    type: 'sword',
    color: 0xffc857,
    shape: 'diamond',
    radius: 8,
    offset: new Phaser.Math.Vector2(30, 24),
  },
  mage: {
    type: 'mage',
    color: 0xb56cff,
    shape: 'triangle',
    radius: 9,
    offset: new Phaser.Math.Vector2(38, -26),
  },
  nurse: {
    type: 'nurse',
    color: 0x61d88f,
    shape: 'circle',
    radius: 7,
    offset: new Phaser.Math.Vector2(-24, -24),
  },
  engineer: {
    type: 'engineer',
    color: 0xff9f45,
    shape: 'square',
    radius: 8,
    offset: new Phaser.Math.Vector2(44, 10),
  },
  assassin: {
    type: 'assassin',
    color: 0xff5d73,
    shape: 'diamond',
    radius: 7,
    offset: new Phaser.Math.Vector2(-42, 4),
  },
  tank: {
    type: 'tank',
    color: 0x8aa0a8,
    shape: 'circle',
    radius: 10,
    offset: new Phaser.Math.Vector2(0, 34),
  },
  summoner: {
    type: 'summoner',
    color: 0x5bd5d1,
    shape: 'triangle',
    radius: 8,
    offset: new Phaser.Math.Vector2(0, -38),
  },
};

export function getPartnerSpec(type: PartnerType) {
  const spec = PARTNER_SPECS[type];
  if (!spec) {
    throw new Error(`Unknown partner type: ${type}`);
  }
  return spec;
}

export function createPartnerGraphic(scene: Phaser.Scene, type: PartnerType, x: number, y: number): PartnerShape {
  const spec = getPartnerSpec(type);

  if (spec.shape === 'square') {
    const square = scene.add.rectangle(x, y, spec.radius * 2, spec.radius * 2, spec.color, 1);
    square.setStrokeStyle(2, 0xffffff, 0.78);
    square.setDepth(22);
    return square;
  }

  if (spec.shape === 'triangle') {
    const triangle = scene.add.triangle(
      x,
      y,
      0,
      -spec.radius,
      spec.radius,
      spec.radius,
      -spec.radius,
      spec.radius,
      spec.color,
      1,
    );
    triangle.setStrokeStyle(2, 0xffffff, 0.78);
    triangle.setDepth(22);
    return triangle;
  }

  if (spec.shape === 'diamond') {
    const diamond = scene.add.rectangle(x, y, spec.radius * 1.8, spec.radius * 1.8, spec.color, 1);
    diamond.setStrokeStyle(2, 0xffffff, 0.78);
    diamond.setRotation(Math.PI / 4);
    diamond.setDepth(22);
    return diamond;
  }

  const arc = scene.add.circle(x, y, spec.radius, spec.color, 1);
  arc.setStrokeStyle(2, 0xffffff, 0.78);
  arc.setDepth(22);

  return arc;
}

export function getHomePosition(player: Phaser.GameObjects.Arc, partner: PartnerRuntime, slotIndex: number) {
  const spec = getPartnerSpec(partner.type);
  const duplicateShift = new Phaser.Math.Vector2((slotIndex % 3) * 9 - 9, Math.floor(slotIndex / 3) * 9);
  return {
    x: player.x + spec.offset.x + duplicateShift.x,
    y: player.y + spec.offset.y + duplicateShift.y,
  };
}

export function distancePointToSegmentSq(px: number, py: number, ax: number, ay: number, bx: number, by: number) {
  const abx = bx - ax;
  const aby = by - ay;
  const apx = px - ax;
  const apy = py - ay;
  const abLengthSq = abx * abx + aby * aby;
  if (abLengthSq <= 0) {
    return Phaser.Math.Distance.Squared(px, py, ax, ay);
  }

  const t = Phaser.Math.Clamp((apx * abx + apy * aby) / abLengthSq, 0, 1);
  const cx = ax + abx * t;
  const cy = ay + aby * t;
  return Phaser.Math.Distance.Squared(px, py, cx, cy);
}
